import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Button,
} from "@/components/primitives";
import { ArrowRight, Target, TrendingUp, Zap, Calendar } from "lucide-react";
import { TopPriorityAction } from "@/components/TopPriorityAction";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import GaugeComponent from "react-gauge-component";
import {
	calculateReflectionProgress,
	type ReflectionGoal,
	type ReflectionProgress,
} from "@/lib/reflection-progress";

interface IntroStepProps {
	onStartCheckIn: () => void;
}

export function IntroStep({ onStartCheckIn }: IntroStepProps) {
	const { user, profile } = useAuth();
	const [progress, setProgress] = useState<ReflectionProgress | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchProgress = async () => {
			if (!user || !profile?.reflection_goal) {
				setLoading(false);
				return;
			}

			setLoading(true);
			const result = await calculateReflectionProgress(
				user.id,
				profile.reflection_goal as ReflectionGoal,
			);
			setProgress(result);
			setLoading(false);
		};

		fetchProgress();
	}, [user, profile?.reflection_goal]);

	const getStatusText = () => {
		if (!progress) return null;
		switch (progress.status) {
			case "behind":
				return "Keep going";
			case "on-target":
				return "Great, on target!";
			case "smashing-it":
				return "Smashing it!";
		}
	};

	const getStatusIcon = () => {
		if (!progress) return null;
		switch (progress.status) {
			case "behind":
				return <Target className="h-5 w-5" />;
			case "on-target":
				return <TrendingUp className="h-5 w-5" />;
			case "smashing-it":
				return <Zap className="h-5 w-5" />;
		}
	};

	const getStatusColor = () => {
		if (!progress) return "";
		switch (progress.status) {
			case "behind":
				return "text-gray-600 dark:text-gray-400";
			case "on-target":
				return "text-green-600 dark:text-green-400";
			case "smashing-it":
				return "text-green-600 dark:text-green-400";
		}
	};

	const getGaugeValue = () => {
		if (!progress) return 0;
		const maxValue = 150;
		const greenStart = 0.7 * maxValue; // start of green zone
		const onTargetStart = greenStart;
		const onTargetEnd = 100; // fill through green up to 100

		const getWeeklyRange = (g: ReflectionGoal) => {
			switch (g) {
				case "1-2":
					return { min: 1, max: 2 };
				case "3-5":
					return { min: 3, max: 5 };
				case "6+":
					return { min: 6, max: 7 };
			}
			return { min: 1, max: 2 };
		};

		// Get user's weekly goal from profile
		const { min, max } = getWeeklyRange(
			(profile?.reflection_goal as ReflectionGoal) || "3-5",
		);
		const avg = progress.weeklyAverage;

		if (avg <= 0) return 0;

		if (avg >= 7) return maxValue;

		if (avg < min) {
			const ratio = Math.max(0, Math.min(1, avg / min));
			return ratio * greenStart;
		}

		if (avg <= max) {
			const span = Math.max(0.0001, max - min);
			const ratio = (avg - min) / span;
			return onTargetStart + ratio * (onTargetEnd - onTargetStart);
		}

		// Smashing it: pin to maximum
		return maxValue;
	};

	const getGaugeColors = () => {
		return [
			{ color: "#ff7d7dff", length: 0.33 }, // Softer red (red-300)
			{ color: "#ffdf60ff", length: 0.34 }, // Softer amber (amber-200)
			{ color: "#6ae497ff", length: 0.33 }, // Softer green (green-300)
		];
	};

	const today = new Date();
	const dayOfWeek = today.toLocaleDateString("en-US", { weekday: "long" });
	const dateStr = today.toLocaleDateString("en-US", {
		month: "long",
		day: "numeric",
		year: "numeric",
	});

	return (
		<div className="max-w-4xl mx-auto">
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				<div className="lg:col-span-2">
					<Card className="border-2 shadow-lg bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm">
						<CardHeader className="pb-4">
							<div className="flex items-center gap-4 mb-4">
								<div className="p-3 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md">
									<Calendar className="h-8 w-8 text-white" />
								</div>
								<div className="flex-1">
									<CardTitle className="text-2xl font-bold text-foreground mb-1">
										Welcome to Your Daily Check-in
									</CardTitle>
									<CardDescription className="text-base">
										{dayOfWeek}, {dateStr}
									</CardDescription>
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-6">
							<div className="prose prose-sm dark:prose-invert max-w-none">
								<p className="text-muted-foreground leading-relaxed">
									Take a few moments to reflect on your day. This daily practice
									helps you track your progress, understand patterns in your
									wellbeing, and stay connected to your goals.
								</p>
							</div>

							<div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 rounded-lg p-4 border border-blue-100 dark:border-blue-900/30">
								<h4 className="font-semibold text-sm mb-2 text-blue-900 dark:text-blue-100">
									What to expect:
								</h4>
								<ul className="space-y-1.5 text-sm text-blue-800 dark:text-blue-200">
									<li className="flex items-start gap-2">
										<span className="text-blue-600 dark:text-blue-400 mt-0.5">
											•
										</span>
										<span>Share how you're feeling today</span>
									</li>
									<li className="flex items-start gap-2">
										<span className="text-blue-600 dark:text-blue-400 mt-0.5">
											•
										</span>
										<span>Describe your day and experiences</span>
									</li>
									<li className="flex items-start gap-2">
										<span className="text-blue-600 dark:text-blue-400 mt-0.5">
											•
										</span>
										<span>Reflect on your active reflections (optional)</span>
									</li>
								</ul>
							</div>

							<Button
								size="lg"
								onClick={onStartCheckIn}
								className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
							>
								Begin Today's Check-in
								<ArrowRight className="h-5 w-5 ml-2" />
							</Button>
						</CardContent>
					</Card>
				</div>

				<div className="space-y-4">
					<TopPriorityAction variant="checkin" />
				</div>
			</div>
		</div>
	);
}
